import { graphql } from 'react-relay';
import { JiraResolutionAri } from '@atlassian/ari/jira/resolution';
import type { resolutionQuery as resolutionQueryType } from '@atlassian/jira-relay/src/__generated__/resolutionQuery.graphql';

import { FieldValueLookupError } from '../../../common/errors';
import type { PublicToInternalPropertyTransformerContext } from '../../../common/types';
import type {
	FieldOptionsFilter,
	OptionsVisibility,
} from '../../../common/types/options-visibility';
import { fetchFieldOptions } from '../../../common/utils/fetch-field-options';
import { getResourceIdfromAri } from '../../../common/utils/get-resource-id-from-ari';
import { isNotEmptyString } from '../../../common/utils/is-not-empty-string';

type ResolutionPublicGetValue = null | {
	id: string;
	value: string;
};

export type ResolutionPublicSetValue = null | string;

type ResolutionInternalValue =
	| {
			value: string;
			label: string;
	  }
	| null
	| undefined;

// we don't add validator in config for value field till we add support for setValue,
// if we add validator it will run publicToInternalTransformer,
// and if there is no such transformer it will pass income value to the component
// for more details see codebase
// src/packages/issue-adjustments/issue-adjustments/src/controllers/issue-adjustments/utils/supported-field-properties/index.tsx line 174
export const resolutionSetValueValidator = (value: unknown): value is ResolutionPublicSetValue =>
	value === null || isNotEmptyString(value);

export const resolutionGetValueTransformer = (
	internalValue: ResolutionInternalValue,
): ResolutionPublicGetValue => {
	if (!internalValue) {
		return null;
	}

	const { label, value } = internalValue;

	return {
		id: getResourceIdfromAri(value),
		value: label,
	};
};

// TODO: remove the 'resolutions' field from the query on
// 'ui-modifications-its-resolution-set-value-with-respect-to-transition-id_fkb2h' clean-up
// eslint-disable-next-line @atlassian/relay/must-use-inline
export const resolutionQuery = graphql`
	query resolutionQuery(
		$ari: ID!
		$filterById: JiraFieldOptionIdsFilterInput
		$transitionId: String!
		$isResolutionForTransition: Boolean!
	) {
		node(id: $ari) {
			... on JiraResolutionField {
				# eslint-disable-next-line @atlassian/relay/unused-fields
				resolutions(filterById: $filterById) @skip(if: $isResolutionForTransition) {
					edges {
						node {
							value: id
							label: name
						}
					}
				}
				# eslint-disable-next-line @atlassian/relay/unused-fields
				resolutionsForTransition(filterById: $filterById, transitionId: $transitionId)
					@include(if: $isResolutionForTransition)
					@optIn(to: "JiraResolutionsForTransition") {
					edges {
						node {
							value: id
							label: name
						}
					}
				}
			}
		}
	}
`;

export const resolutionSetValueTransformer = async (
	inputValue: ResolutionPublicSetValue,
	_lookupValues: undefined,
	{
		tenantContext,
		viewContext,
		environment,
		fieldContext,
	}: PublicToInternalPropertyTransformerContext,
): Promise<ResolutionInternalValue> => {
	if (!inputValue) return null;

	const resolutionARI = JiraResolutionAri.create({
		resolutionId: inputValue,
		siteId: tenantContext.cloudId,
		activationId: tenantContext.activationId,
	}).toString();

	const isResolutionForTransition = !!viewContext.transitionId;

	const response: {
		edges?: [
			{
				node?: {
					label?: string | null;
					value: string;
				};
			},
		];
	} = await fetchFieldOptions<resolutionQueryType>({
		tenantContext,
		viewContext,
		environment,
		fieldContext,
		optionARIs: [resolutionARI],
		nodePropertyName: isResolutionForTransition ? 'resolutionsForTransition' : 'resolutions',
		queryName: 'FETCH_RESOLUTION',
		query: resolutionQuery,
		extraQueryVariables: {
			transitionId: viewContext.transitionId ?? '',
			isResolutionForTransition,
		},
	});

	const resolution = response?.edges?.[0]?.node;

	if (resolution?.value !== resolutionARI) {
		throw new FieldValueLookupError(inputValue);
	}

	return {
		value: resolution.value,
		label: resolution.label ?? '',
	};
};

export const transformSetOptionsVisibilityAriResolution = (
	{ isVisible, options }: OptionsVisibility,
	_lookupValues: undefined,
	{ tenantContext }: PublicToInternalPropertyTransformerContext,
): FieldOptionsFilter => ({
	operation: isVisible ? 'ALLOW' : 'EXCLUDE',
	optionIds: options.map((optionId) =>
		JiraResolutionAri.create({
			resolutionId: optionId,
			siteId: tenantContext.cloudId,
			activationId: tenantContext.activationId,
		}).toString(),
	),
});
